import React, { useState } from "react";
import HubHeader from "../HubHeader";
import HubTaskbar from "../HubTaskbar";
import "../../../assets/css/Hub.css";
import "../../../assets/css/HubBilling.css";

export default function HubBilling() {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [currentPlan] = useState<string>("free"); // Current user plan

  const plans = [
    {
      id: "free",
      name: "Free",
      description: "Get started with basics",
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        "5 AMP executions/month",
        "1 custom AMP",
        "Community support",
        "Standard processing",
      ],
      buttonText: "Current Plan",
      highlighted: false,
    },
    {
      id: "personal",
      name: "Personal",
      description: "For individual power users",
      monthlyPrice: 12,
      yearlyPrice: 120,
      features: [
        "50 AMP executions/month",
        "5 custom AMPs",
        "Email support",
        "Fast processing speed",
        "Basic analytics",
      ],
      buttonText: "Upgrade to Personal",
      highlighted: false,
    },
    {
      id: "pro",
      name: "Pro",
      description: "For professionals & teams",
      monthlyPrice: 29,
      yearlyPrice: 290,
      features: [
        "Unlimited AMP executions",
        "Unlimited custom AMPs",
        "Priority support",
        "Fastest processing",
        "Advanced analytics",
        "API access",
        "Team collaboration",
        "Custom integrations",
      ],
      buttonText: "Upgrade to Pro",
      highlighted: true,
      popular: true,
    },
    {
      id: "enterprise",
      name: "Enterprise",
      description: "For large organizations",
      monthlyPrice: null,
      yearlyPrice: null,
      features: [
        "Everything in Pro",
        "Dedicated account manager",
        "Custom AI training",
        "99.9% SLA guarantee",
        "On-premise deployment",
        "Advanced security",
        "SSO & compliance",
        "Custom contracts",
      ],
      buttonText: "Contact Sales",
      highlighted: false,
    },
  ];

  const handlePlanSelection = (planId: string) => {
    if (planId === "enterprise") {
      console.log("Contact sales for enterprise plan");
    } else if (planId !== currentPlan) {
      console.log(`Switching to ${planId} plan`);
    }
  };

  return (
    <div className="hub-billing-layout">
      <HubHeader />
      
      <div className="hub-billing-body">
        <aside className="hub-billing-sidebar-wrap taskbar-only" aria-label="Navigation">
          <HubTaskbar />
        </aside>
        
        <main className="hub-billing-main-content">
          <div className="hub-billing-inner">
            <div className="hub-billing-header">
              <h1 className="hub-billing-title">Choose Your Plan</h1>
              <p className="hub-billing-subtitle">
                Select the perfect plan for your needs. Upgrade or downgrade at any time.
              </p>

              <div className="hub-billing-toggle">
                <button
                  className={`hub-toggle-btn ${billingCycle === "monthly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("monthly")}
                >
                  Monthly
                </button>
                <button
                  className={`hub-toggle-btn ${billingCycle === "yearly" ? "active" : ""}`}
                  onClick={() => setBillingCycle("yearly")}
                >
                  Yearly
                  <span className="hub-toggle-badge">Save 17%</span>
                </button>
              </div>
            </div>

            <div className="hub-plans-container">
              {plans.map((plan) => (
              <div
                key={plan.id}
                className={`hub-plan-card ${plan.highlighted ? "highlighted" : ""} ${
                  currentPlan === plan.id ? "current" : ""
                }`}
              >
                {plan.popular && (
                  <div className="hub-plan-badge">Most Popular</div>
                )}

                <div className="hub-plan-header">
                  <h3 className="hub-plan-name">{plan.name}</h3>
                  <p className="hub-plan-description">{plan.description}</p>
                </div>

                <div className="hub-plan-price">
                  {plan.monthlyPrice === null ? (
                    <div className="hub-price-custom">
                      <span className="hub-price-text">Custom</span>
                    </div>
                  ) : (
                    <>
                      <span className="hub-price-currency">$</span>
                      <span className="hub-price-amount">
                        {billingCycle === "monthly"
                          ? plan.monthlyPrice
                          : Math.floor(plan.yearlyPrice / 12)}
                      </span>
                      <span className="hub-price-period">/month</span>
                    </>
                  )}
                </div>

                {billingCycle === "yearly" && plan.yearlyPrice !== null && (
                  <p className="hub-plan-yearly-note">
                    Billed ${plan.yearlyPrice} annually
                  </p>
                )}

                <button
                  className={`hub-plan-button ${
                    currentPlan === plan.id ? "current-plan" : ""
                  }`}
                  onClick={() => handlePlanSelection(plan.id)}
                  disabled={currentPlan === plan.id}
                >
                  {currentPlan === plan.id ? "Current Plan" : plan.buttonText}
                </button>

                <div className="hub-plan-features">
                  <p className="hub-features-title">What's included:</p>
                  <ul className="hub-features-list">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="hub-feature-item">
                        <svg className="hub-feature-icon" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
            </div>

            <div className="hub-billing-footer">
              <p className="hub-billing-note">
                All plans include secure payment processing and can be cancelled at any time.
              </p>
              <p className="hub-billing-contact">
                Need help choosing? <a href="#contact">Contact our sales team</a>
              </p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
